"use client";

import type { Category, Product, SearchFilters } from "@workspace/ui/types";
import type { Dispatch } from "react";
import React, { createContext, useContext, useMemo, useReducer } from "react";

import { FeaturedProductsAPI, ProductDetailAPI } from "@/const/endPoint";
import { buildSearchUrl, snakeToCamelCaseObject } from "@/utils";
import axiosInstance from "@/utils/api/fetchData/axiosInstance";

export enum CategoryActionType {
  START_CATEGORY = "START_CATEGORY",
  SUCCESS_CATEGORY = "SUCCESS_CATEGORY",
  FAILURE_CATEGORY = "FAILURE_CATEGORY",
  SET_SELECTED_CATEGORY = "SET_SELECTED_CATEGORY",
  SET_EXPANDED_CATEGORIES = "SET_EXPANDED_CATEGORIES",
  TOGGLE_CATEGORY = "TOGGLE_CATEGORY",
  SET_EXPANDED_SUBCATEGORIES = "SET_EXPANDED_SUBCATEGORIES",
  TOGGLE_SUBCATEGORY = "TOGGLE_SUBCATEGORY",
  SET_EXPANDED_SUBSUBCATEGORIES = "SET_EXPANDED_SUBSUBCATEGORIES",
  TOGGLE_SUBSUBCATEGORY = "TOGGLE_SUBSUBCATEGORY",
  SET_ACTIVE_CATEGORY = "SET_ACTIVE_CATEGORY",
  START_LOAD_MORE = "START_LOAD_MORE",
  SUCCESS_LOAD_MORE = "SUCCESS_LOAD_MORE",
  FAILURE_LOAD_MORE = "FAILURE_LOAD_MORE",

  // * Product
  START_PRODUCT = "START_PRODUCT",
  SUCCESS_PRODUCT = "SUCCESS_PRODUCT",
  FAILURE_PRODUCT = "FAILURE_PRODUCT",

  // Search Filters
  SET_SEARCH_FILTERS = "SET_SEARCH_FILTERS",

  // Featured Products
  START_FEATURED_PRODUCTS = "START_FEATURED_PRODUCTS",
  SUCCESS_FEATURED_PRODUCTS = "SUCCESS_FEATURED_PRODUCTS",
  FAILURE_FEATURED_PRODUCTS = "FAILURE_FEATURED_PRODUCTS",
}

export type CategoryState = {
  category: Category | null;
  selectedCategory: string | null;
  loadingCategory: boolean;
  errorCategory: string | null;
  expandedCategories: Record<string, boolean>;
  expandedSubcategories: Record<string, boolean>;
  expandedSubSubcategories: Record<string, boolean>;
  activeCategory: string;
  loadingMore: boolean;
  hasMorePages: boolean;
  currentPage: number;

  // * Product
  product: Product | null;
  accessories: Product[];
  loadingProduct: boolean;
  errorProduct: string | null;

  // * Search Filters
  searchFilters: SearchFilters;

  // * Featured Products
  featuredProducts: Product[];
  loadingFeaturedProducts: boolean;
  errorFeaturedProducts: string | null;
};

const CategoryStateContext = createContext<CategoryState | undefined>(
  undefined,
);

type CategoryAction =
  | { type: CategoryActionType.START_CATEGORY }
  | {
      type: CategoryActionType.SUCCESS_CATEGORY;
      category: Category | null;
    }
  | { type: CategoryActionType.FAILURE_CATEGORY; error: string }
  | {
      type: CategoryActionType.SET_SELECTED_CATEGORY;
      selectedCategory: string | null;
    }
  | {
      type: CategoryActionType.SET_EXPANDED_CATEGORIES;
      expandedCategories: Record<string, boolean>;
    }
  | {
      type: CategoryActionType.TOGGLE_CATEGORY;
      categorySlug: string;
    }
  | {
      type: CategoryActionType.SET_EXPANDED_SUBCATEGORIES;
      expandedSubcategories: Record<string, boolean>;
    }
  | {
      type: CategoryActionType.TOGGLE_SUBCATEGORY;
      subcategorySlug: string;
    }
  | {
      type: CategoryActionType.SET_EXPANDED_SUBSUBCATEGORIES;
      expandedSubSubcategories: Record<string, boolean>;
    }
  | {
      type: CategoryActionType.TOGGLE_SUBSUBCATEGORY;
      subSubcategorySlug: string;
    }
  | {
      type: CategoryActionType.SET_ACTIVE_CATEGORY;
      activeCategory: string;
    }
  | { type: CategoryActionType.START_LOAD_MORE }
  | {
      type: CategoryActionType.SUCCESS_LOAD_MORE;
      newProducts: any[];
      pagination: any;
    }
  | { type: CategoryActionType.FAILURE_LOAD_MORE; error: string }
  // * Product
  | { type: CategoryActionType.START_PRODUCT }
  | {
      type: CategoryActionType.SUCCESS_PRODUCT;
      product: Product | null;
      accessories: Product[];
    }
  | { type: CategoryActionType.FAILURE_PRODUCT; error: string }

  // * Search Filters
  | { type: CategoryActionType.SET_SEARCH_FILTERS; filters: SearchFilters }

  // * Featured Products
  | { type: CategoryActionType.START_FEATURED_PRODUCTS }
  | {
      type: CategoryActionType.SUCCESS_FEATURED_PRODUCTS;
      featuredProducts: Product[];
    }
  | { type: CategoryActionType.FAILURE_FEATURED_PRODUCTS; error: string };

type CategoryDispatch = Dispatch<CategoryAction>;

const CategoryDispatchContext = createContext<CategoryDispatch | undefined>(
  undefined,
);

const initialSearchFilters: SearchFilters = {
  colors: [],
  sizes: [],
  features: [],
  minPrice: null,
  maxPrice: null,
  onSaleOnly: false,
  sort: "default",
};

const initialCategoryState: CategoryState = {
  category: null,
  selectedCategory: null,
  loadingCategory: false,
  errorCategory: null,
  expandedCategories: {
    "odziez-damska": true,
    "odziez-meska": false,
    "odziez-dziecieca": false,
  },
  expandedSubcategories: {},
  expandedSubSubcategories: {},
  activeCategory: "",
  loadingMore: false,
  hasMorePages: true,
  currentPage: 1,

  // * Product
  product: null,
  accessories: [],
  loadingProduct: false,
  errorProduct: null,

  // * Search Filters
  searchFilters: initialSearchFilters,

  // * Featured Products
  featuredProducts: [],
  loadingFeaturedProducts: false,
  errorFeaturedProducts: null,
};

function categoryReducer(
  state: CategoryState,
  action: CategoryAction,
): CategoryState {
  switch (action.type) {
    case CategoryActionType.START_CATEGORY:
      return {
        ...state,
        loadingCategory: true,
        currentPage: 1,
        hasMorePages: true,
      };
    case CategoryActionType.SUCCESS_CATEGORY:
      return {
        ...state,
        category: action.category,
        loadingCategory: false,
        errorCategory: null,
        hasMorePages: action.category?.pagination
          ? action.category.pagination.currentPage <
            action.category.pagination.pagesCount
          : false,
      };
    case CategoryActionType.FAILURE_CATEGORY:
      return {
        ...state,
        loadingCategory: false,
        errorCategory: action.error,
      };
    case CategoryActionType.START_LOAD_MORE:
      return {
        ...state,
        loadingMore: true,
      };
    case CategoryActionType.SUCCESS_LOAD_MORE:
      return {
        ...state,
        category: state.category
          ? {
              ...state.category,
              products: [...state.category.products, ...action.newProducts],
              pagination: action.pagination,
            }
          : null,
        loadingMore: false,
        currentPage: action.pagination?.currentPage || state.currentPage + 1,
        hasMorePages: action.pagination
          ? action.pagination.currentPage < action.pagination.pagesCount
          : false,
      };
    case CategoryActionType.FAILURE_LOAD_MORE:
      return {
        ...state,
        loadingMore: false,
        errorCategory: action.error,
      };
    case CategoryActionType.SET_SELECTED_CATEGORY:
      return {
        ...state,
        selectedCategory: action.selectedCategory,
      };
    case CategoryActionType.SET_EXPANDED_CATEGORIES:
      return {
        ...state,
        expandedCategories: action.expandedCategories,
      };
    case CategoryActionType.TOGGLE_CATEGORY:
      return {
        ...state,
        expandedCategories: {
          ...state.expandedCategories,
          [action.categorySlug]: !state.expandedCategories[action.categorySlug],
        },
      };
    case CategoryActionType.SET_EXPANDED_SUBCATEGORIES:
      return {
        ...state,
        expandedSubcategories: action.expandedSubcategories,
      };
    case CategoryActionType.TOGGLE_SUBCATEGORY:
      return {
        ...state,
        expandedSubcategories: {
          ...state.expandedSubcategories,
          [action.subcategorySlug]:
            !state.expandedSubcategories[action.subcategorySlug],
        },
      };
    case CategoryActionType.SET_EXPANDED_SUBSUBCATEGORIES:
      return {
        ...state,
        expandedSubSubcategories: action.expandedSubSubcategories,
      };
    case CategoryActionType.TOGGLE_SUBSUBCATEGORY:
      return {
        ...state,
        expandedSubSubcategories: {
          ...state.expandedSubSubcategories,
          [action.subSubcategorySlug]:
            !state.expandedSubSubcategories[action.subSubcategorySlug],
        },
      };
    case CategoryActionType.SET_ACTIVE_CATEGORY:
      return {
        ...state,
        activeCategory: action.activeCategory,
      };

    // * Product
    case CategoryActionType.START_PRODUCT:
      return {
        ...state,
        loadingProduct: true,
      };
    case CategoryActionType.SUCCESS_PRODUCT:
      return {
        ...state,
        product: action.product,
        accessories: action.accessories,
        loadingProduct: false,
        errorProduct: null,
      };
    case CategoryActionType.FAILURE_PRODUCT:
      return {
        ...state,
        loadingProduct: false,
        errorProduct: action.error,
      };

    // * Search Filters
    case CategoryActionType.SET_SEARCH_FILTERS:
      return {
        ...state,
        searchFilters: action.filters,
      };

    // * Featured Products
    case CategoryActionType.START_FEATURED_PRODUCTS:
      return {
        ...state,
        loadingFeaturedProducts: true,
      };
    case CategoryActionType.SUCCESS_FEATURED_PRODUCTS:
      return {
        ...state,
        featuredProducts: action.featuredProducts,
        loadingFeaturedProducts: false,
        errorFeaturedProducts: null,
      };
    case CategoryActionType.FAILURE_FEATURED_PRODUCTS:
      return {
        ...state,
        loadingFeaturedProducts: false,
        errorFeaturedProducts: action.error,
      };

    default:
      throw new Error("Unhandled action");
  }
}

export function CategoryProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(categoryReducer, initialCategoryState);

  return (
    <CategoryDispatchContext.Provider value={dispatch}>
      <CategoryStateContext.Provider value={useMemo(() => state, [state])}>
        {children}
      </CategoryStateContext.Provider>
    </CategoryDispatchContext.Provider>
  );
}

export function useCategoryState() {
  const state = useContext(CategoryStateContext);
  if (!state) throw new Error("CategoryStateContext not found");
  return state;
}

export function useCategoryDispatch() {
  const dispatch = useContext(CategoryDispatchContext);
  if (!dispatch) throw new Error("CategoryDispatchContext not found");
  return dispatch;
}

const getCategoryData = async (
  slug: string,
  page: number,
  filters: SearchFilters,
  dispatch: CategoryDispatch,
) => {
  dispatch({ type: CategoryActionType.START_CATEGORY });

  const url = buildSearchUrl(slug, page, "", filters, "category");
  const response = await axiosInstance.get(url);

  if (response.data.psdata) {
    const categoryData = snakeToCamelCaseObject(response.data.psdata);

    dispatch({
      type: CategoryActionType.SUCCESS_CATEGORY,
      category: snakeToCamelCaseObject(categoryData),
    });

    return categoryData;
  } else {
    dispatch({
      type: CategoryActionType.FAILURE_CATEGORY,
      error: response.data.error,
    });
  }
};

const loadMoreProducts = async (
  slug: string,
  page: number,
  filters: SearchFilters,
  dispatch: CategoryDispatch,
) => {
  dispatch({ type: CategoryActionType.START_LOAD_MORE });

  try {
    const url = buildSearchUrl(slug, page, "", filters, "category");
    const response = await axiosInstance.get(url);

    if (response.data.psdata) {
      const categoryData = snakeToCamelCaseObject(response.data.psdata);
      dispatch({
        type: CategoryActionType.SUCCESS_LOAD_MORE,
        newProducts: categoryData.products || [],
        pagination: categoryData.pagination,
      });
    } else {
      dispatch({
        type: CategoryActionType.FAILURE_LOAD_MORE,
        error: response.data.error || "No data returned",
      });
    }
  } catch (error) {
    dispatch({
      type: CategoryActionType.FAILURE_LOAD_MORE,
      error: `Network error occurred: ${error}`,
    });
  }
};

const getProductData = async (
  reference: string,
  dispatch: CategoryDispatch,
) => {
  dispatch({ type: CategoryActionType.START_PRODUCT });

  const response = await axiosInstance.get(`/rest${ProductDetailAPI}`, {
    params: { reference },
  });

  if (response.data.psdata) {
    dispatch({
      type: CategoryActionType.SUCCESS_PRODUCT,
      product: snakeToCamelCaseObject(response.data.psdata),
      accessories: snakeToCamelCaseObject(response.data.accessories || []),
    });

    return {
      combinations: snakeToCamelCaseObject(
        Object.values(response.data.ulamshopavailability.combinations),
      ),
      attributes: snakeToCamelCaseObject(
        response.data.ulamshopavailability.attributes_groups,
      ),
      idProduct: response.data.psdata.id,
      url: response.data.psdata.url,
      parentCategory: response.data.psdata.parent_category,
    };
  } else {
    dispatch({
      type: CategoryActionType.FAILURE_PRODUCT,
      error: response.data.error,
    });
  }
};

const getFeaturedProducts = async (
  dispatch: CategoryDispatch,
  idProduct?: string,
) => {
  dispatch({ type: CategoryActionType.START_FEATURED_PRODUCTS });

  try {
    const response = await axiosInstance.get(`/rest${FeaturedProductsAPI}`, {
      params: {
        id_product: idProduct,
      },
    });

    if (response.data.psdata) {
      dispatch({
        type: CategoryActionType.SUCCESS_FEATURED_PRODUCTS,
        featuredProducts: snakeToCamelCaseObject(response.data.psdata),
      });
    } else {
      dispatch({
        type: CategoryActionType.FAILURE_FEATURED_PRODUCTS,
        error: response.data.error,
      });
    }
  } catch (error) {
    dispatch({
      type: CategoryActionType.FAILURE_FEATURED_PRODUCTS,
      error: `Network error occurred: ${error}`,
    });
  }
};

export function useCategory() {
  const state = useCategoryState();
  const dispatch = useCategoryDispatch();

  const getCategoryDataFunc = (
    slug: string,
    page: number,
    filters: SearchFilters,
  ) => getCategoryData(slug, page, filters, dispatch);

  const loadMoreProductsFunc = (
    slug: string,
    page: number,
    filters: SearchFilters,
  ) => loadMoreProducts(slug, page, filters, dispatch);

  const setSelectedCategory = (selectedCategory: string | null) =>
    dispatch({
      type: CategoryActionType.SET_SELECTED_CATEGORY,
      selectedCategory,
    });

  const setExpandedCategories = (expandedCategories: Record<string, boolean>) =>
    dispatch({
      type: CategoryActionType.SET_EXPANDED_CATEGORIES,
      expandedCategories,
    });

  const toggleCategory = (categorySlug: string) =>
    dispatch({
      type: CategoryActionType.TOGGLE_CATEGORY,
      categorySlug,
    });

  const setExpandedSubcategories = (
    expandedSubcategories: Record<string, boolean>,
  ) =>
    dispatch({
      type: CategoryActionType.SET_EXPANDED_SUBCATEGORIES,
      expandedSubcategories,
    });

  const toggleSubcategory = (subcategorySlug: string) =>
    dispatch({
      type: CategoryActionType.TOGGLE_SUBCATEGORY,
      subcategorySlug,
    });

  const toggleSubSubcategory = (subSubcategorySlug: string) =>
    dispatch({
      type: CategoryActionType.TOGGLE_SUBSUBCATEGORY,
      subSubcategorySlug,
    });

  const setActiveCategory = (activeCategory: string) =>
    dispatch({
      type: CategoryActionType.SET_ACTIVE_CATEGORY,
      activeCategory,
    });

  const getProductDataFunc = (reference: string) =>
    getProductData(reference, dispatch);

  const getFeaturedProductsFunc = (idProduct?: string) =>
    getFeaturedProducts(dispatch, idProduct);

  const clearProductData = () => {
    dispatch({
      type: CategoryActionType.SUCCESS_PRODUCT,
      product: null,
      accessories: [],
    });
  };

  return {
    state,
    getCategoryData: getCategoryDataFunc,
    loadMoreProducts: loadMoreProductsFunc,
    setSelectedCategory,
    setExpandedCategories,
    toggleCategory,
    setExpandedSubcategories,
    toggleSubcategory,
    toggleSubSubcategory,
    setActiveCategory,
    getProductData: getProductDataFunc,
    getFeaturedProducts: getFeaturedProductsFunc,
    clearProductData,
  };
}
